import { useCallback } from 'react'
import { useAppSelector } from 'state/store'
import { ExecutionState } from 'types/workflow'
import useWorkflow from 'modules/interaction/hooks/useWorkflow'
import { useTranslation } from 'react-i18n-lite'
import { isEmbedded, useParentPostMessage } from '@yes.technology/react-toolkit'
import { ActionButtonActionData } from 'types/shared'

const useActionButtonNavigation = () => {
  const { t } = useTranslation()

  const workflowUuid = useAppSelector(
    (state) => state.interactions.interactionModel.workflowUuid
  )
  const { workflow } = useWorkflow({ workflowUuid, t })

  const flatData = useAppSelector(
    (state) => state.interactions.interactionModel.flatData
  )

  const interpolateUrl = useCallback(
    (url: string): string => {
      if (!url) return ''

      let interpolatedUrl = url

      if (workflow?.uuid) {
        interpolatedUrl = interpolatedUrl.replace(
          /\{\{PROCESS_UUID\}\}/g,
          workflow.uuid
        )
        interpolatedUrl = interpolatedUrl.replace(
          /\{\{WORKFLOW_UUID\}\}/g,
          workflow.uuid
        )
      }

      if (flatData?.uuid) {
        interpolatedUrl = interpolatedUrl.replace(
          /\{\{INTERACTION_UUID\}\}/g,
          flatData.uuid
        )
      }

      if (flatData?.activity_values) {
        const matchKey = interpolatedUrl.match(/{{activity-(.*?)}}/)
        const activityModelUuid = matchKey ? matchKey[1] : ''

        const replaceRegex = new RegExp(
          `\\{\\{activity-${activityModelUuid}\\}\\}`,
          'g'
        )

        const activityValue = flatData.activity_values[activityModelUuid]

        let valueStr = typeof activityValue === 'string' ? activityValue : ''

        if (typeof activityValue === 'object') {
          if ('id' in activityValue) valueStr = activityValue.id
          else valueStr = JSON.stringify(activityValue)
        }

        interpolatedUrl = interpolatedUrl.replace(replaceRegex, valueStr)
      }

      if (workflow?.execution_state) {
        try {
          const executionState = JSON.parse(
            workflow.execution_state
          ) as ExecutionState
          if (executionState?.data_container) {
            const matchKey = interpolatedUrl.match(/{{process-data-(.*?)}}/)
            const dataContainerKey = matchKey ? matchKey[1] : ''
            const dataContainerVariable =
              executionState.data_container[dataContainerKey]

            if (dataContainerVariable !== undefined) {
              const pattern = new RegExp(
                `\\{\\{process-data-${dataContainerKey}\\}\\}`,
                'g'
              )
              interpolatedUrl = interpolatedUrl.replace(
                pattern,
                dataContainerVariable
              )
            }
          }
        } catch (error) {
          console.error('Error parsing process execution_state:', error)
        }
      }

      return interpolatedUrl
    },
    [flatData, workflow]
  )

  const { postMessage } = useParentPostMessage()

  const navigateToUrl = useCallback(
    (actionData: ActionButtonActionData) => {
      const interpolatedUrl = interpolateUrl(actionData.url)

      if (actionData.open_in_new_tab) {
        window.open(interpolatedUrl, '_blank', 'noopener,noreferrer')
        return
      }

      if (interpolatedUrl) {
        if (isEmbedded()) {
          postMessage({ type: 'redirect', url: interpolatedUrl })
        } else {
          window.location.href = interpolatedUrl
        }
      }
    },
    [interpolateUrl, postMessage]
  )

  return {
    interpolateUrl,
    navigateToUrl
  }
}

export default useActionButtonNavigation
